import { Play } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import type { MessageAttachmentDto } from "@multi-chat/shared";
import { cn } from "../../../lib/utils.js";
import type { InboxImageLayout } from "../MessageMediaImage/MessageMediaImage.js";
import { MessageMediaImageSkeleton } from "../MessageMediaImage/MessageMediaImageSkeleton.js";
import { MessageVideoPreview } from "../MessageVideoPreview/MessageVideoPreview.js";
import { INBOX_ATTACHMENT_IMAGE_SHELL_CLASS, INBOX_ATTACHMENT_IMAGE_THUMB_CLASS } from "../inbox-image-frame.js";
import { formatMediaClock } from "../media-clock.js";
import { useInboxAttachmentUrl } from "../use-inbox-attachment-url.js";

export function MessageVideo({
  organizationId,
  attachment,
  layout = "default",
}: {
  organizationId: number;
  attachment: MessageAttachmentDto;
  layout?: InboxImageLayout;
}): React.JSX.Element {
  const { t } = useTranslation();
  const { url, urlResolving, failed, ensureUrl, refreshUrl } = useInboxAttachmentUrl(
    organizationId,
    attachment,
  );
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [duration, setDuration] = useState<number | null>(null);
  const [frameReady, setFrameReady] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [displayUrl, setDisplayUrl] = useState(url);
  const isBubble = layout === "bubble";

  useEffect(() => {
    setDisplayUrl(url);
    setDuration(null);
    setFrameReady(false);
  }, [url]);

  const handleLoadedMetadata = useCallback(
    (event: React.SyntheticEvent<HTMLVideoElement>) => {
      const node = event.currentTarget;
      if (Number.isFinite(node.duration) && node.duration > 0) {
        setDuration(node.duration);
      }
      if (node.currentTime < 0.05) {
        node.currentTime = 0.05;
      }
    },
    [],
  );

  const openPreview = useCallback(async () => {
    if (!displayUrl) return;
    if (ensureUrl) {
      const fresh = await ensureUrl();
      if (fresh) {
        setDisplayUrl(fresh);
      }
    }
    setPreviewOpen(true);
  }, [displayUrl, ensureUrl]);

  const shellClass = cn(
    INBOX_ATTACHMENT_IMAGE_SHELL_CLASS,
    "w-fit max-w-full",
    isBubble ? "rounded-[inherit]" : "rounded-md",
    !isBubble && "my-1",
    "bg-ink/90",
  );

  if (failed) {
    return (
      <div className={cn(shellClass, "flex items-center justify-center px-3")}>
        <p className="m-0 text-[0.8125rem] text-ink-subtle">{t("inbox.media.unavailable")}</p>
      </div>
    );
  }

  if (urlResolving || !displayUrl) {
    return <MessageMediaImageSkeleton layout={layout} label={t("inbox.media.processing")} />;
  }

  return (
    <>
      <div className={cn(shellClass, "relative")}>
        {!frameReady ? (
          <MessageMediaImageSkeleton overlay layout={layout} label={t("inbox.media.processing")} />
        ) : null}
        <button
          type="button"
          className={cn(
            "relative z-[2] block border-0 bg-transparent p-0 leading-none",
            "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent",
          )}
          onClick={() => void openPreview()}
          aria-label={t("inbox.media.preview.videoOpen")}
        >
          <video
            ref={videoRef}
            src={displayUrl}
            preload="metadata"
            playsInline
            muted
            onLoadedMetadata={handleLoadedMetadata}
            onSeeked={() => setFrameReady(true)}
            onLoadedData={() => setFrameReady(true)}
            onError={() => void refreshUrl()}
            className={cn(
              INBOX_ATTACHMENT_IMAGE_THUMB_CLASS,
              isBubble ? "rounded-[inherit]" : "rounded-md",
            )}
          />
          <span
            className="pointer-events-none absolute inset-0 z-[3] flex items-center justify-center"
            aria-hidden="true"
          >
            <span className="flex size-12 items-center justify-center rounded-full bg-ink/65 text-surface-elevated shadow-md backdrop-blur-[2px]">
              <Play className="size-6 translate-x-px fill-current" />
            </span>
          </span>
          {duration != null ? (
            <span className="pointer-events-none absolute start-1.5 top-1.5 z-[3] rounded-full bg-ink/60 px-1.5 py-0.5 text-[10px] tabular-nums text-surface-elevated">
              {formatMediaClock(duration)}
            </span>
          ) : null}
        </button>
      </div>
      {previewOpen && displayUrl ? (
        <MessageVideoPreview
          open={previewOpen}
          onOpenChange={setPreviewOpen}
          videoUrl={displayUrl}
          fileName={attachment.fileName}
          mimeType={attachment.mimeType}
          organizationId={organizationId}
          attachmentId={attachment.attachmentId}
          ensureUrl={ensureUrl}
        />
      ) : null}
    </>
  );
}
